"use client";

import { useForm } from "@tanstack/react-form";
import { useDebouncedCallback } from "use-debounce";
import dayjs from "dayjs";
import { toast } from "sonner";
import { useEffect, useRef, useTransition } from "react";
import {
  invoiceSchema,
  type InvoiceData,
  type InvoiceItemData,
  PDF_DATA_LOCAL_STORAGE_KEY,
} from "@/app/schema";
import { updateAppMetadata } from "@/app/(app)/utils/get-app-metadata";

const DEBOUNCE_TIMEOUT = 500;

interface UseInvoiceFormProps {
  invoiceData: InvoiceData;
  onSubmit: (data: InvoiceData) => void;
  isMobile: boolean;
  setInvoiceFormHasErrors: (hasErrors: boolean) => void;
}

// Extended item with calculated fields
interface CalculatedItem extends InvoiceItemData {
  netAmount: number;
  vatAmount: number;
  preTaxAmount: number;
}

/**
 * Custom hook for managing invoice form state with proper data flow
 *
 * PERFORMANCE OPTIMIZATIONS:
 * 1. Uses useTransition for non-urgent updates (localStorage, parent notifications)
 * 2. Debounced saves to reduce frequent updates
 * 3. Memoized calculations for item totals
 *
 * CRITICAL: This hook ALWAYS propagates data to the parent component,
 * even when validation fails. This ensures the PDF preview always
 * reflects the current form state.
 */
export function useInvoiceForm({
  invoiceData,
  onSubmit,
  isMobile,
  setInvoiceFormHasErrors,
}: UseInvoiceFormProps) {
  // Use refs to track latest callback references to avoid stale closures
  const onSubmitRef = useRef(onSubmit);
  const isMobileRef = useRef(isMobile);
  const setInvoiceFormHasErrorsRef = useRef(setInvoiceFormHasErrors);

  // Ref to track last items state for optimization
  const lastItemsHashRef = useRef<string>("");

  // useTransition for non-urgent updates (saving to localStorage, analytics)
  const [, startTransition] = useTransition();

  // Update refs when props change
  useEffect(() => {
    onSubmitRef.current = onSubmit;
    isMobileRef.current = isMobile;
    setInvoiceFormHasErrorsRef.current = setInvoiceFormHasErrors;
  }, [onSubmit, isMobile, setInvoiceFormHasErrors]);

  const form = useForm({
    defaultValues: invoiceData,
    validators: {
      onChange: ({ value }) => {
        const result = invoiceSchema.safeParse(value);
        if (!result.success) {
          return result.error.issues.map((issue) => ({
            message: issue.message,
            path: issue.path.map(String),
          }));
        }
        return undefined;
      },
    },
    onSubmit: async ({ value }) => {
      onSubmit(value);
      updateAppMetadata((current) => ({
        ...current,
        invoiceLastUpdatedAt: dayjs().toISOString(),
      }));
    },
  });

  // Debounced save to localStorage and parent
  // Uses useTransition for non-urgent work to keep INP low
  const debouncedSave = useDebouncedCallback(async (data: InvoiceData) => {
    // Immediate update to parent for PDF preview (high priority)
    onSubmitRef.current(data);

    // Use transition for non-urgent background work
    startTransition(() => {
      // Clear any existing error toasts
      toast.dismiss("form-errors-error-toast");
      toast.dismiss("invoice-has-changed-toast");
      toast.dismiss("invalid-invoice-url-error-toast");

      setInvoiceFormHasErrorsRef.current(false);

      try {
        const validationResult = invoiceSchema.safeParse(data);

        if (!validationResult.success) {
          setInvoiceFormHasErrorsRef.current(true);
          return;
        }

        // Only save to localStorage if validation passes
        localStorage.setItem(
          PDF_DATA_LOCAL_STORAGE_KEY,
          JSON.stringify(validationResult.data),
        );
      } catch {
        // Silent fail - data already sent to parent
      }
    });
  }, DEBOUNCE_TIMEOUT);

  // Subscribe to form changes
  useEffect(() => {
    const subscription = form.store.subscribe((state) => {
      debouncedSave(state.values);
    });
    return () => subscription.unsubscribe();
  }, [form.store, debouncedSave]);

  // Auto-calculate totals when items change
  // Optimized to only recalculate when items actually change, not on every form update
  // Uses yieldToMain to prevent blocking interactions (INP optimization)
  useEffect(() => {
    const unsubscribe = form.store.subscribe((state) => {
      const items = state.values.items;

      // Fast check - only calculate hash if items exist
      if (!items?.length) {
        if (state.values.total !== 0) {
          form.setFieldValue("total", 0);
        }
        return;
      }

      // Create a lightweight hash using only the fields that affect calculations
      // Convert to Number first since form values can be strings
      const itemsHash = items
        .map(
          (item) =>
            `${Number(item?.amount || 0).toFixed(2)}|${Number(item?.netPrice || 0).toFixed(2)}`,
        )
        .join(",");

      // Skip if items haven't changed
      if (itemsHash === lastItemsHashRef.current) {
        return;
      }
      lastItemsHashRef.current = itemsHash;

      // Use startTransition for calculation updates to avoid blocking UI
      startTransition(() => {
        // Calculate total
        const total = items.reduce(
          (sum, item) => sum + (item?.amount || 0) * (item?.netPrice || 0),
          0,
        );
        const formattedTotal = Number(total.toFixed(2));
        if (state.values.total !== formattedTotal) {
          form.setFieldValue("total", formattedTotal);
        }

        // Recalculate item totals - only for items that changed
        items.forEach((item, index) => {
          if (!item) return;

          const amount = Number(item.amount) || 0;
          const netPrice = Number(item.netPrice) || 0;
          const netAmount = Number((amount * netPrice).toFixed(2));

          // Only update if values have changed
          const currentItem = items[index] as Partial<CalculatedItem>;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const formApi = form as any;

          if (currentItem.netAmount !== netAmount) {
            formApi.setFieldValue(`items[${index}].netAmount`, netAmount);
            formApi.setFieldValue(`items[${index}].preTaxAmount`, netAmount);
          }
        });
      });
    });

    return () => unsubscribe.unsubscribe();
  }, [form, startTransition]);

  return {
    form,
  };
}
